/**
 * The arch: the Classic board bent into an upside-down U round the playing
 * surface.
 *
 * Pure arithmetic, no React and no DOM, so the numbers can be asserted rather
 * than screenshotted. `Arch.tsx` draws what this file places.
 *
 * Graduated from `dev/sketches/classic-kit.tsx`, the concept Mitch kept on
 * 2026-09-16 and the six finishes he picked the same day. The board starts at
 * the foot of the left leg, climbs, crosses the top and comes down the right
 * leg to one home hole at its foot, shared by every lane. A lane per side,
 * yours outside. The open bottom faces the hand.
 *
 * | | Width | Height | Corner | Lane gap | Peg |
 * |---|---|---|---|---|---|
 * | Phone | 358 | 316 | 44 | 13 | 5.5 |
 * | Laptop | 560 | 430 | 60 | 19 | 8 |
 *
 * The phone's 358 is a 390 phone less its 16-point margins; the laptop's 560
 * is what a six-card fan at the 116-point hand card needs.
 */

import type { ArchFinish } from '~/store/boardFinishes.js';

/** Which drawing: the phone's or the laptop's. */
export type ArchSize = 'phone' | 'laptop';

/** One size's numbers, each tuned against the others. */
export interface ArchGeometry {
  /** The whole board's box. */
  readonly w: number;
  readonly h: number;
  /** The outer lane's corner at the top of each leg. */
  readonly r: number;
  /** From the box's edge to the outer lane. */
  readonly inset: number;
  /** Between two sides' lanes. */
  readonly laneGap: number;
  /** An ordinary hole's radius. */
  readonly dot: number;
  /** The front peg's radius. */
  readonly peg: number;
  /** The numerals' size: 11 draws `text-xs`, 12 `text-sm`. */
  readonly label: number;
}

export const ARCH: Readonly<Record<ArchSize, ArchGeometry>> = {
  phone: { w: 358, h: 316, r: 44, inset: 10, laneGap: 13, dot: 2, peg: 5.5, label: 11 },
  laptop: { w: 560, h: 430, r: 60, inset: 16, laneGap: 19, dot: 3, peg: 8, label: 12 },
};

/** The finishes are a saved setting, so their list lives in the state band. */
export { ARCH_FINISHES, DEFAULT_FINISH, type ArchFinish } from '~/store/boardFinishes.js';

/** A point on a lane, with the outward normal there. */
export interface Spot {
  readonly x: number;
  readonly y: number;
  readonly nx: number;
  readonly ny: number;
}

/** The streets a player counts by: every thirty, short of home. */
export function streets(target: number): readonly number[] {
  return [30, 60, 90].filter((hole) => hole < target);
}

/** The outer lane's length, foot to foot. */
export function archLength(s: ArchGeometry): number {
  const W = s.w - 2 * s.inset;
  const H = s.h - 2 * s.inset;
  return 2 * (H - s.r) + (W - 2 * s.r) + Math.PI * s.r;
}

/** A point `dist` along the outer lane from the stall, with the outward normal. */
export function archSpot(s: ArchGeometry, dist: number): Spot {
  const left = s.inset;
  const top = s.inset;
  const right = s.w - s.inset;
  const bottom = s.h - s.inset;
  const R = s.r;
  const leg = bottom - top - R;
  const across = right - left - 2 * R;
  const arc = (Math.PI / 2) * R;
  const arcAt = (ccx: number, ccy: number, from: number, along: number): Spot => {
    const angle = ((from + (along / arc) * 90) * Math.PI) / 180;
    const nx = Math.cos(angle);
    const ny = Math.sin(angle);
    return { x: ccx + R * nx, y: ccy + R * ny, nx, ny };
  };
  let d = dist;
  if (d < leg) return { x: left, y: bottom - d, nx: -1, ny: 0 };
  d -= leg;
  if (d < arc) return arcAt(left + R, top + R, 180, d);
  d -= arc;
  if (d < across) return { x: left + R + d, y: top, nx: 0, ny: -1 };
  d -= across;
  if (d < arc) return arcAt(right - R, top + R, 270, d);
  d -= arc;
  return { x: right, y: top + R + d, nx: 1, ny: 0 };
}

/**
 * One lane's point for a hole: the stall at the foot of the left leg, home at
 * the foot of the right. Home is one hole, between the lanes, because a game
 * ends in one place and not in one place per side.
 *
 * @param lane 0 is the outer lane, which is yours.
 */
export function archLane(
  s: ArchGeometry,
  hole: number,
  lane: number,
  lanes: number,
  target: number,
): Spot {
  const spot = archSpot(s, (Math.min(hole, target) / target) * archLength(s));
  const pull = hole >= target ? (s.laneGap * (lanes - 1)) / 2 : s.laneGap * lane;
  return { ...spot, x: spot.x - spot.nx * pull, y: spot.y - spot.ny * pull };
}

/**
 * One lane of the arch as a path, for what runs under the holes. The right
 * leg stops at its last hole and bends in to the home hole, so both lanes are
 * seen to end in the one place. A fractional lane draws the midline.
 * `overrun` carries both feet past the arch's foot, so a wide channel holds
 * the stall and the home hole inside it instead of cutting through them.
 */
export function archPath(
  s: ArchGeometry,
  lane: number,
  lanes: number,
  target: number,
  overrun = 0,
): string {
  const pull = s.laneGap * lane;
  const left = s.inset + pull;
  const top = s.inset + pull;
  const right = s.w - s.inset - pull;
  const bottom = s.h - s.inset;
  const foot = bottom + overrun;
  const R = s.r - pull;
  const pitch = archLength(s) / target;
  const homeX = s.w - s.inset - (s.laneGap * (lanes - 1)) / 2;
  return [
    `M ${String(left)} ${String(foot)}`,
    `L ${String(left)} ${String(top + R)}`,
    `A ${String(R)} ${String(R)} 0 0 1 ${String(left + R)} ${String(top)}`,
    `L ${String(right - R)} ${String(top)}`,
    `A ${String(R)} ${String(R)} 0 0 1 ${String(right)} ${String(top + R)}`,
    `L ${String(right)} ${String(bottom - pitch)}`,
    `L ${String(homeX)} ${String(bottom)}`,
    `L ${String(homeX)} ${String(foot)}`,
  ].join(' ');
}

/**
 * A channel finish: one band round the arch at the midline, wide enough for
 * every lane and its pegs, ending flat past the foot of each leg.
 */
export function isChannel(finish: ArchFinish): boolean {
  return finish !== 'dots' && finish !== 'ticks';
}

/** How far a channel runs past the foot, so the stall and home sit inside it. */
export function channelOverrun(s: ArchGeometry, finish: ArchFinish): number {
  return isChannel(finish) ? s.peg + 4 : 0;
}

/**
 * A channel's rounded foot: a stroke cannot round two corners of its end, so
 * the stroke stops a radius short of the foot and this filled cap finishes it,
 * overlapping the stroke by a point so no seam shows. Rounded the way a
 * card's corners are (Mitch, 2026-09-18).
 */
export function footCap(cx: number, width: number, foot: number, r: number): string {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  const top = foot - r - 1;
  return [
    `M ${String(x0)} ${String(top)}`,
    `L ${String(x1)} ${String(top)}`,
    `L ${String(x1)} ${String(foot - r)}`,
    `A ${String(r)} ${String(r)} 0 0 1 ${String(x1 - r)} ${String(foot)}`,
    `L ${String(x0 + r)} ${String(foot)}`,
    `A ${String(r)} ${String(r)} 0 0 1 ${String(x0)} ${String(foot - r)}`,
    'Z',
  ].join(' ');
}

/** One band of a channel finish: its run, its two feet, and how wide it is. */
export interface ChannelBand {
  readonly width: number;
  readonly run: string;
  readonly stallCap: string;
  readonly homeCap: string;
}

/**
 * A channel's band. A lip is the same band drawn wider under the fill; its
 * feet run half its extra width further too, so the end is edged like the
 * sides.
 */
export function channelBand(
  s: ArchGeometry,
  lanes: number,
  target: number,
  finish: ArchFinish,
  extra = 0,
): ChannelBand {
  const overrun = channelOverrun(s, finish);
  const width = s.laneGap * (lanes - 1) + s.peg * 2 + 6 + extra;
  const foot = s.h - s.inset + overrun + extra / 2;
  const r = s.dot * 3 + extra / 2;
  const midline = (lanes - 1) / 2;
  const stall = s.inset + s.laneGap * midline;
  const home = archLane(s, target, 0, lanes, target);
  return {
    width,
    run: archPath(s, midline, lanes, target, overrun + extra / 2 - r),
    stallCap: footCap(stall, width, foot, r),
    homeCap: footCap(home.x, width, foot, r),
  };
}

/** A hole's weight: an ordinary hole, a fifth, or home. */
export type HoleWeight = 0 | 1 | 2;

export function holeRadius(s: ArchGeometry, weight: HoleWeight): number {
  return s.dot + (weight === 0 ? 0 : weight === 1 ? 0.7 : 1.4);
}

/** A line segment. */
export interface Segment {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/**
 * A milestone's two notches, on its own hole: one outside the outer lane and
 * one inside the inner. On a channel the notch grows out of the rail's edge;
 * on a hairline finish it starts off the peg's edge.
 */
export function notches(
  s: ArchGeometry,
  hole: number,
  lanes: number,
  target: number,
  finish: ArchFinish,
): readonly [Segment, Segment] {
  const near = isChannel(finish) ? s.peg + 4 : s.peg;
  const far = near + 4;
  const tick = (spot: Spot, sign: 1 | -1): Segment => ({
    x1: spot.x + spot.nx * sign * near,
    y1: spot.y + spot.ny * sign * near,
    x2: spot.x + spot.nx * sign * far,
    y2: spot.y + spot.ny * sign * far,
  });
  return [
    tick(archLane(s, hole, 0, lanes, target), 1),
    tick(archLane(s, hole, lanes - 1, lanes, target), -1),
  ];
}

/** A numeral's place: where it stands and how it is anchored. */
export interface Numeral {
  readonly x: number;
  readonly y: number;
  readonly anchor: 'start' | 'middle' | 'end';
  readonly baseline: 'middle' | 'auto';
}

/** A street's numeral, inside the arch on its own hole, clear of the notch. */
export function streetNumeral(
  s: ArchGeometry,
  hole: number,
  lanes: number,
  target: number,
  finish: ArchFinish,
): Numeral {
  const spot = archLane(s, hole, 0, lanes, target);
  const push = -(s.laneGap * (lanes - 1) + s.peg + (isChannel(finish) ? 11 : 7));
  const x = spot.x + spot.nx * push;
  const y = spot.y + spot.ny * push;
  const sideways = Math.abs(spot.nx) > Math.abs(spot.ny);
  const outward = spot.ny * push > 0;
  return {
    x,
    y: sideways ? y : y + (outward ? s.label * 0.9 : 0),
    anchor: sideways ? (spot.nx * push < 0 ? 'end' : 'start') : 'middle',
    baseline: sideways ? 'middle' : 'auto',
  };
}

/** Home's numeral, under the home hole and the channel's foot. */
export function homeNumeral(
  s: ArchGeometry,
  lanes: number,
  target: number,
  finish: ArchFinish,
): Numeral {
  const home = archLane(s, target, 0, lanes, target);
  const homeDot = holeRadius(s, 2);
  return {
    x: home.x,
    y: home.y + channelOverrun(s, finish) + homeDot + 4 + s.label * 0.9,
    anchor: 'middle',
    baseline: 'auto',
  };
}

/* --------------------------------------------------------------- the field */

/**
 * Where the field starts inside the arch, by lane count, as the kit ranged it.
 *
 * The field is centered inside the arch's rails by its inset, rounded to the
 * 4-point grid. On the two-lane phone the inside is 293 and the field is 260
 * (80 + 16 + 164), so center is 49 in; 48 is a point left of it, and 56 sat it
 * visibly right (Mitch, 2026-09-18). On the laptop the inside is 466 and the
 * field 408, so 80 is within a point of center. Three lanes hang the inner
 * lane's "60" a lane deeper, so the field starts that much lower.
 */
export const FIELD_INSET: Readonly<Record<ArchSize, Readonly<Record<2 | 3, string>>>> = {
  phone: { 2: 'top-14 pl-12', 3: 'top-17 pl-14' },
  laptop: { 2: 'top-18 pl-20', 3: 'top-23 pl-20' },
};

/** The inset's lane count: two lanes, or the three-handed table's three. */
export function fieldLanes(lanes: number): 2 | 3 {
  return lanes >= 3 ? 3 : 2;
}
